import type { KlReportColumnProperties } from '@shared/components/kl-report/kl-report-datatable.models';

/** Scalar or nested object values read from dynamic grid rows. */
export type GridRowCellValue = string | number | boolean | Date | null | undefined | object;

export function isRowObject(
  value: GridRowCellValue | object | null | undefined
): value is Record<string, GridRowCellValue> {
  return value != null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
}

/** Supports `binding` paths like `branch.description` for nested row objects. */
export type KlStatusBadgeTone = 'danger' | 'info' | 'warn' | 'success' | 'neutral' | 'locked';

export function cellRawValue(row: object | null | undefined, binding: string): GridRowCellValue {
  if (row == null || binding === '') {
    return undefined;
  }
  if (!isRowObject(row)) {
    return undefined;
  }
  if (!binding.includes('.')) {
    return row[binding];
  }
  return binding.split('.').reduce<GridRowCellValue>((acc, key) => {
    if (!isRowObject(acc)) {
      return undefined;
    }
    return acc[key];
  }, row);
}

export interface GridColumn {
  header?: string;
  binding?: string;
  width?: number | string;
  visible?: boolean;
  align?: string;
  cssClass?: string;
  format?: 'date' | 'date-time' | 'time' | 'image' | 'string';
  /** Fixed fractional digits for numeric cell display (legacy `f2`/`f4`/`n2` amounts). */
  displayFractionDigits?: number;
  /** Thousands separator for numeric cell display (legacy `n2` shows grouping, `f2` does not). */
  numberGrouping?: boolean;
  translate?: boolean;
  badge?: boolean;
  /** Maps row enum → kl-status-badge tone. When set with badge, renders .kl-status-badge instead of p-tag. */
  badgeTones?: Record<string, KlStatusBadgeTone>;
  multi_select?: boolean;
  arrayToString?: boolean;
  arrayObjectToString?: boolean;
  objectProperty?: string;
  translateItem?: boolean;
  calculateFromArray?: boolean;
  fontCss?: { style: string; color: string };
  hideColumnHeader?: boolean;
  icon?: boolean;
  icon_key?: boolean;
  icon_info?: boolean;
  button?: boolean;
  rowActionName?: string;
  aggregate?: string;
  /** Report field properties the user set for this column (column settings drawer with `showColumnProperties`). */
  columnProperties?: KlReportColumnProperties;
}

/** A column as stored in a saved grid design's `content.columns`. */
export interface GridDesignColumn {
  binding: string;
  visible: boolean;
  header?: string;
  width?: number | string;
  /** Report column type: `n0`..`n5` (number with decimals), `dd.MM.yyyy`/`date`, `string`; other values are ignored. */
  format?: string;
  /** Report footer aggregate code 1..5 (Sum, Cnt, Avg, Max, Min); other values are ignored. */
  aggregate?: number | string;
}

export interface EndpointConfig {
  endpoint: string;
  name?: string;
  /** Full path used for delete/restriction GET instead of `endpoint` (e.g. `sales-invoice/header`). */
  specialGetByIdEndpoint?: string;
  /** Optional endpoint override for edit-condition check GET. */
  editConditionCheckEndpoint?: string;
  /** Child segment appended to `{endpoint}/{id}/…` for edit-condition check (e.g. `header`). */
  editConditionCheckChildEndpoint?: string;
  filterChild?: string;
  specialType?: string;
  hasFilter?: boolean;
  microApiService?: string;
  branchRequired?: boolean;
  skipEditLock?: boolean;
}

export interface FilterableItem {
  name: string;
  description: string;
  parameterName?: string;
  objectKey?: string;
  type?: 'string' | 'number' | 'array' | 'object';
  disabledExpression?: boolean;
}

export interface InitState {
  label: string;
  payload: any;
  authNum?: string;
  child?: InitState[];
}

export interface EditOption {
  objectProperty?: string;
  objectValue?: any;
  alertMessage?: string;
  alertProperty?: string;
}

export interface DeleteOption {
  objectProperty?: string;
  objectValue: any;
  alertMessage?: string;
  alertProperty?: string;
}

export interface DeleteConfirm {
  condition?: { values?: string[]; control?: string };
  delete_related?: string;
  deleteWithConfirm?: boolean;
  inputPlaceholder?: string;
  skipConfirmation?: boolean;
}

/** Context passed to {@link ActionDto.method} from `kl-grid-container` (row / toolbar actions). */
export interface GridActionContext {
  item: any;
  /** Re-runs current filter (parent `kl-grid`); set when list loads from API. */
  refresh?: () => void;
  /** Optional overlay tied to parent grid loading signals (e.g. lock/unlock). */
  gridLoading?: {
    show: (translationKey: string) => void;
    hide: () => void;
  };
  showAlert?: (message: string, type?: 'error' | 'warn' | 'warning' | 'success' | 'info') => void;
  handleError?: (error: unknown) => void;
}

export interface ActionDto {
  slug: string;
  slug_2?: string;
  icon?: string;
  method?: (ctx: GridActionContext) => void;
  separator?: boolean;
  children?: ActionDto[];
  authNum?: string;
  filterValue?: string[];
  notShowInToolbar?: boolean;
  notShowInContextMenu?: boolean;
  showChild?: boolean;
  /** When true, action runs without requiring a selected grid row (toolbar stays enabled on empty grids). */
  gridLevel?: boolean;
  /** When true, toolbar button is disabled regardless of row selection. */
  disabled?: boolean;
  /** Extra per-row eligibility check (e.g. only show for a specific `document_type`); evaluated against the current/right-clicked row. */
  isRowVisible?: (row: GridActionContext['item']) => boolean;
}

/** Common `kl-grid` page config used by finance card and movement list components. */
export type FinanceListPageConfig = {
  columns: GridColumn[];
  authNum: string;
  filterSelectItems: FilterableItem[];
  filterObject: Record<string, unknown>;
  deleteOptions: DeleteOption[];
  editOptions?: EditOption[];
  initStates?: InitState[];
  actions?: ActionDto[];
  dialogName: string;
  endpointConfig: EndpointConfig;
};

export interface PagingQuery {
  paging: {
    limit: number;
    offset: number;
    fetch?: string;
    total?: number;
  };
  sort?: Array<{ name: string; desc?: boolean }>;
}

export interface FilterPayload {
  filter: Record<string, any>;
  query: PagingQuery;
  parameters?: any[];
}

/** Toolbar export scope: current page, all rows matching active filters, or all rows (host scope only). */
export type KlGridExportScope = 'page' | 'filtered' | 'all';

/** Minimal row shape a `multiPicker` dialog (e.g. `MaterialList`, `ServiceCardList`) hands back
 *  through its "select and continue" callback — every picker endpoint returns at least these. */
export interface PickerSelectionRow {
  id?: number | string;
  code?: string;
  description?: string;
}

/** Saved fast-filter `design` record; `id` may be a string (Mongo) or a number. */
export interface FastFilterDesignRecord {
  id?: number | string;
  module?: string;
  key?: string;
  type?: string;
  content?: string;
}
